import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  query,
  where,
  orderBy,
  deleteDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

export default function SavingLedger({ user }) {

  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [memberId, setMemberId] = useState("");
  const [ledger, setLedger] = useState([]);
  const [currentBalance, setCurrentBalance] = useState(0);

  const [particular, setParticular] = useState("");
  const [deposit, setDeposit] = useState("");
  const [withdrawal, setWithdrawal] = useState("");
  const [entryDateTime, setEntryDateTime] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  /* ================= MEMBERS ================= */
  useEffect(() => {
    const loadMembers = async () => {
      const snap = await getDocs(collection(db, "members"));
      setMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    loadMembers();
  }, []);

  const filteredMembers = members.filter(m =>
    m.memberId?.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.name?.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.mobile?.includes(memberSearch)
  );

  /* ================= LEDGER ================= */
  const loadLedger = async (mid) => {
    if (!mid) return;

    const q = query(
      collection(db, "savingLedger"),
      where("memberId", "==", mid),
      orderBy("createdAt", "desc") // 🔥 NEWEST FIRST
    );

    const snap = await getDocs(q);
    const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setLedger(data);

    // 🔥 CURRENT BALANCE
    let credit = 0;
    let debit = 0;

    data.forEach(l => {
      credit += Number(l.deposit || 0);
      debit += Number(l.withdrawal || 0);
    });

    setCurrentBalance(credit - debit);
  };

  useEffect(() => {
    loadLedger(memberId);
  }, [memberId]);

  /* ================= ADD ENTRY ================= */
  const addEntry = async () => {
    if (!memberId || !particular) {
      alert("Member & Particular required");
      return;
    }

    const dep = Number(deposit || 0);
    const wit = Number(withdrawal || 0);

    if (dep <= 0 && wit <= 0) {
      alert("Enter Deposit or Withdrawal");
      return;
    }

    const member = members.find(m => m.memberId === memberId);

    const createdAt = entryDateTime
      ? Timestamp.fromDate(new Date(entryDateTime))
      : Timestamp.now();

    await addDoc(collection(db, "savingLedger"), {
      memberId,
      memberName: member?.name || "",
      particular,
      deposit: dep,
      withdrawal: wit,
      createdAt,
      voucherId: "TXN" + Date.now(),
      entryBy: user?.name || "Admin",
    });

    setParticular("");
    setDeposit("");
    setWithdrawal("");
    setEntryDateTime("");

    loadLedger(memberId);
  };

  /* ================= DELETE ================= */
  const deleteEntry = async (id) => {
    if (user.role !== "ADMIN") {
      alert("No permission");
      return;
    }
    if (!window.confirm("Delete this entry?")) return;

    await deleteDoc(doc(db, "savingLedger", id));
    loadLedger(memberId);
  };

  /* ================= FILTER ================= */
  const filteredLedger = ledger.filter(l => {
    if (!l.createdAt) return false;
    const d = l.createdAt.toDate();
    const f = fromDate ? new Date(fromDate) : null;
    const t = toDate ? new Date(toDate + "T23:59:59") : null;
    return (!f || d >= f) && (!t || d <= t);
  });

  /* ================= RUNNING BALANCE ================= */
  let runningBalance = currentBalance;

  const ledgerWithBalance = filteredLedger.map(l => {
    const row = { ...l, runningBalance };
    runningBalance -= Number(l.deposit || 0);
    runningBalance += Number(l.withdrawal || 0);
    return row;
  });

  /* ================= PDF ================= */
  const printPDF = () => {
    const pdf = new jsPDF();
    pdf.text("Saving Account Ledger", 14, 10);

    pdf.autoTable({
      startY: 20,
      head: [[
        "Sr", "Date & Time", "Particular",
        "Withdrawal", "Deposit", "Balance",
        "Voucher", "Entry By"
      ]],
      body: ledgerWithBalance.map((l, i) => [
        i + 1,
        l.createdAt.toDate().toLocaleString(),
        l.particular,
        l.withdrawal || 0,
        l.deposit || 0,
        l.runningBalance,
        l.voucherId,
        l.entryBy,
      ]),
    });

    pdf.save("SavingLedger.pdf");
  };

  /* ================= EXCEL ================= */
  const downloadExcel = () => {
    const data = ledgerWithBalance.map((l, i) => ({
      Sr: i + 1,
      DateTime: l.createdAt.toDate().toLocaleString(),
      Particular: l.particular,
      Withdrawal: l.withdrawal,
      Deposit: l.deposit,
      Balance: l.runningBalance,
      Voucher: l.voucherId,
      EntryBy: l.entryBy,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ledger");
    XLSX.writeFile(wb, "SavingLedger.xlsx");
  };

  return (
    <div className="container-fluid">
      <h4>Saving Account Ledger</h4>

      {/* 🔥 CURRENT BALANCE */}
      {memberId && (
        <div className="mb-2 fw-bold">
          Current Balance: ₹ {currentBalance}
        </div>
      )}

      {/* SEARCH */}
      <div className="row mb-3">
        <div className="col-md-3">
          <input className="form-control"
            placeholder="Search Member ID / Name / Mobile"
            value={memberSearch}
            onChange={e => setMemberSearch(e.target.value)} />
        </div>

        <div className="col-md-3">
          <select className="form-control"
            value={memberId}
            onChange={e => setMemberId(e.target.value)}>
            <option value="">Select Member</option>
            {filteredMembers.map(m => (
              <option key={m.id} value={m.memberId}>
                {m.memberId} - {m.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ENTRY */}
      <div className="row mb-3">
        <div className="col-md-2">
          <input className="form-control" placeholder="Particular"
            value={particular} onChange={e => setParticular(e.target.value)} />
        </div>
        <div className="col-md-2">
          <input className="form-control" placeholder="Deposit"
            value={deposit} onChange={e => setDeposit(e.target.value)} />
        </div>
        <div className="col-md-2">
          <input className="form-control" placeholder="Withdrawal"
            value={withdrawal} onChange={e => setWithdrawal(e.target.value)} />
        </div>
        <div className="col-md-3">
          <input type="datetime-local" className="form-control"
            value={entryDateTime}
            onChange={e => setEntryDateTime(e.target.value)} />
        </div>
        <div className="col-md-2">
          <button className="btn btn-primary w-100" onClick={addEntry}>
            Add Entry
          </button>
        </div>
      </div>

      {/* FILTER + EXPORT */}
      <div className="row mb-2">
        <div className="col-md-2">
          <input type="date" className="form-control"
            onChange={e => setFromDate(e.target.value)} />
        </div>
        <div className="col-md-2">
          <input type="date" className="form-control"
            onChange={e => setToDate(e.target.value)} />
        </div>
        <div className="col-md-2">
          <button className="btn btn-success w-100" onClick={downloadExcel}>
            Excel
          </button>
        </div>
        <div className="col-md-2">
          <button className="btn btn-danger w-100" onClick={printPDF}>
            PDF
          </button>
        </div>
      </div>

      {/* TABLE */}
      <table className="table table-bordered table-sm">
        <thead className="table-dark text-center">
          <tr>
            <th>Sr</th>
            <th>Date & Time</th>
            <th>Particular</th>
            <th>Withdrawal</th>
            <th>Deposit</th>
            <th>Balance</th>
            <th>Voucher</th>
            <th>Entry By</th>
            {user.role === "ADMIN" && <th>Action</th>}
          </tr>
        </thead>

        <tbody>
          {ledgerWithBalance.map((l, i) => (
            <tr key={l.id} className="text-center">
              <td>{i + 1}</td>
              <td>{l.createdAt.toDate().toLocaleString()}</td>
              <td>{l.particular}</td>
              <td>{l.withdrawal || 0}</td>
              <td>{l.deposit || 0}</td>
              <td>{l.runningBalance}</td>
              <td>{l.voucherId}</td>
              <td>{l.entryBy}</td>
              {user.role === "ADMIN" && (
                <td>
                  <button className="btn btn-sm btn-danger"
                    onClick={() => deleteEntry(l.id)}>
                    Delete
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}